/*
 * Melting Wafers landing — flavor rows (sections/mw-flavors.liquid) and the live Build a Box
 * (sections/mw-box-promo.liquid).
 *
 * Size buttons carry what a row needs (variant id, stock, box flag), so a size change only
 * updates text and attributes. "Add" posts the variant to /cart/add.js; box sizes get the box
 * line property (value "true", same as the Build a Box page), larger sizes are normal items.
 *
 * The box is never stored here: tiles, meter, message, CTA and every "N in your box" note are
 * derived from the cart lines that carry the box property. The section embeds the cart on load;
 * after any change (here, in the cart drawer, or elsewhere) /cart.js is fetched again.
 * The cart drawer is Rebuy Smart Cart on this store, and Rebuy opens itself on every
 * /cart/add.js (its own setting), so after an add the drawer is only opened here when Rebuy is
 * not on the page: the theme's <cart-drawer>, then the cart icon, then /cart.
 *
 * Layout: when the flavor rows render a sidebar slot ([data-mw-box-slot]), the whole Build a Box
 * section element is moved into it, so the box sits in a sticky sidebar beside the rows instead
 * of at the bottom of the page. Only the panel's position changes — its markup, settings and
 * behavior are the section's own. The static panel (mw-box-static) docks the same way; it has no
 * cart state, so docking is all this file does for it.
 */
(() => {
  if (window.mwLandingInit) return;
  window.mwLandingInit = true;

  const ADDED_MS = 1600;
  const DEFAULT_PROPERTY = '_melting_wafer_box';
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const grid = () => document.querySelector('[data-mw-grid]');
  const boxEls = () => document.querySelectorAll('[data-mw-box]');
  const lineProperty = () => (grid() && grid().dataset.lineProperty) || DEFAULT_PROPERTY;
  const fill = (template, values) =>
    Object.keys(values).reduce((s, k) => s.split(`[${k}]`).join(values[k]), template || '');

  const money = (cents, format) => {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (precision, thousands, decimal) => {
      const parts = (cents / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return whole + (parts[1] ? decimal + parts[1] : '');
    };
    let value;
    switch ((format.match(placeholder) || [, 'amount'])[1]) {
      case 'amount_no_decimals': value = withDelimiters(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = withDelimiters(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(0, '.', ','); break;
      default: value = withDelimiters(2, ',', '.');
    }
    return format.replace(placeholder, value);
  };

  const escapeHTML = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const flavorInfo = () => {
    const map = {};
    document.querySelectorAll('script[data-mw-flavors]').forEach((el) => {
      try { Object.assign(map, JSON.parse(el.textContent)); } catch (e) { /* ignore a broken block */ }
    });
    return map;
  };

  const sizeOf = (line) => parseFloat(String(line.variant_title || '').split(' ')[0]) || '';
  const boxLines = (cart) => (cart && cart.items ? cart.items : [])
    .filter((line) => line.properties && line.properties[lineProperty()]);

  /* ---------- Cart helpers ---------- */

  const syncCartUI = (data) => {
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && data && data.sections && data.sections['cart-icon-bubble']) {
      bubble.innerHTML = data.sections['cart-icon-bubble'];
    }
    const rebuy = window.Rebuy;
    if (rebuy && rebuy.Cart && typeof rebuy.Cart.fetchCart === 'function') {
      try { rebuy.Cart.fetchCart(); } catch (e) { /* Rebuy refreshes itself on open */ }
    }
  };

  const openDrawer = (data) => {
    if (window.Rebuy) return; // Rebuy already opened itself for this add.
    const icon = document.getElementById('cart-icon');
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      try { drawer.renderContents(data, true); return; } catch (e) { /* fall through */ }
    }
    if (icon) icon.click();
    else window.location.href = (window.theme && theme.routes && theme.routes.cart) || '/cart';
  };

  const post = async (path, body) => {
    const res = await fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ ...body, sections: 'cart-icon-bubble', sections_url: window.location.pathname }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) throw new Error(data.description || data.message || '');
    return data;
  };

  const getCart = () => fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  /* ---------- Render: box section + row notes ---------- */

  let lastCart = null;

  const renderRowNotes = (cart) => {
    const g = grid();
    if (!g) return;
    const counts = {};
    boxLines(cart).forEach((line) => { counts[line.product_id] = (counts[line.product_id] || 0) + line.quantity; });
    g.querySelectorAll('[data-mw-card]').forEach((card) => {
      const note = card.querySelector('[data-mw-note]');
      if (note) note.dataset.inBox = counts[card.dataset.productId] || 0;
      const btn = card.querySelector('.mw-size[aria-pressed="true"]');
      if (btn) setNote(g, card, btn);
    });
  };

  const renderBoxPanel = (el, cart) => {
    const d = el.dataset;
    const format = d.moneyFormat || '${{amount}}';
    const min = Number(d.min) || 0;
    const lines = boxLines(cart);
    const info = flavorInfo();

    let bags = 0;
    let subtotal = 0;
    lines.forEach((line) => { bags += line.quantity; subtotal += line.original_line_price; });

    const reached = min > 0 && subtotal >= min;
    const values = {
      min: money(min, format).replace(/\.00$/, ''),
      bags,
      subtotal: money(subtotal, format),
      left: money(Math.max(0, min - subtotal), format),
    };

    const fillEl = el.querySelector('[data-mw-fill]');
    if (fillEl) fillEl.style.width = (min ? Math.min(100, (subtotal / min) * 100) : 0) + '%';
    const msg = el.querySelector('[data-mw-msg]');
    const text = !bags ? d.msgEmpty : fill(reached ? d.msgDone : d.msgProgress, values);
    if (msg && msg.textContent !== text) msg.textContent = text;
    const cta = el.querySelector('[data-mw-cta]');
    if (cta) {
      // While building, the button takes the shopper back to the flavors; once the box
      // qualifies it goes to the review page.
      cta.textContent = !bags ? d.ctaEmpty : reached ? d.ctaDone : d.ctaProgress;
      cta.setAttribute('href', reached ? d.reviewUrl : d.buildUrl);
    }
    const clear = el.querySelector('[data-mw-clear]');
    if (clear) clear.hidden = !bags;

    const tiles = el.querySelector('[data-mw-tiles]');
    if (!tiles) return;
    const shown = lines.slice(-6);
    const extra = lines.length - shown.length;
    const filled = shown.map((line, i) => {
      const f = info[line.product_id] || {};
      const name = f.name || line.product_title;
      const img = f.tile || (line.image ? line.image + (line.image.includes('?') ? '&' : '?') + 'width=360' : '');
      const label = i === 0 && extra > 0
        ? fill(d.moreLabel, { count: extra })
        : fill(d.tileLabel, { qty: line.quantity, size: sizeOf(line) });
      return `
        <div class="mw-tile" style="background:${escapeHTML(f.panel || '#fff')}">
          ${img ? `<img src="${escapeHTML(img)}" alt="${escapeHTML(name)}" loading="lazy" width="360" height="400">` : ''}
          <span class="mw-tile-label">${escapeHTML(label)}</span>
          <button class="mw-tile-remove" type="button" data-mw-remove="${escapeHTML(line.key)}" data-qty="${line.quantity}"
            aria-label="${escapeHTML(fill(d.removeLabel, { name }))}">−</button>
        </div>`;
    });
    const empty = Array.from({ length: 6 - shown.length }, () =>
      `<a class="mw-tile mw-tile--empty" href="${escapeHTML(d.flavorsUrl)}" aria-label="${escapeHTML(d.addLabel)}">+</a>`);
    tiles.innerHTML = filled.concat(empty).join('');
  };

  const renderBox = (cart) => boxEls().forEach((el) => renderBoxPanel(el, cart));

  /*
   * Move the Build a Box section into the flavor rows' sidebar slot. The section element itself
   * is moved, so nothing about it changes. The node is remembered because the theme editor
   * re-renders a section by replacing it: after the flavor rows are re-rendered the slot is a new
   * (empty) element and the panel is detached, so it is put back in.
   */
  let boxSection = null;

  const dockBox = () => {
    const live = document.querySelector('[data-mw-box], [data-mw-box-static]');
    if (live) boxSection = live.closest('.shopify-section') || live.closest('.mw-s-box') || live;
    const slot = document.querySelector('[data-mw-box-slot]');
    if (!slot || !boxSection || boxSection.parentElement === slot) return;
    slot.appendChild(boxSection);
  };

  const render = (cart) => {
    lastCart = cart;
    renderBox(cart);
    renderRowNotes(cart);
  };

  let refreshTimer;
  const refresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => getCart().then(render).catch(() => {}), 150);
  };

  /* ---------- Flavor rows ---------- */

  const isBox = (btn) => btn.dataset.box === 'true';
  const selectedSize = (card) => card.querySelector('.mw-size[aria-pressed="true"]');

  function setNote(g, card, btn) {
    const note = card.querySelector('[data-mw-note]');
    if (!note) return;
    const count = Number(note.dataset.inBox) || 0;
    note.textContent = isBox(btn) ? (count ? fill(g.dataset.boxNote, { count }) : '') : g.dataset.bulkNote;
  }

  const setAddButton = (g, card, btn) => {
    const add = card.querySelector('[data-mw-add]');
    if (!add || add.getAttribute('aria-busy') === 'true') return;
    const available = btn.dataset.available === 'true';
    add.classList.remove('is-added');
    add.disabled = !available;
    add.textContent = !available ? g.dataset.soldoutLabel : isBox(btn) ? g.dataset.addBoxLabel : g.dataset.addCartLabel;
  };

  const selectSize = (g, card, btn) => {
    card.querySelectorAll('.mw-size').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    const error = card.querySelector('[data-mw-error]');
    if (error) error.textContent = '';
    setNote(g, card, btn);
    setAddButton(g, card, btn);
  };

  const addToCart = async (g, card, add) => {
    const btn = selectedSize(card);
    if (!btn || btn.dataset.available !== 'true') return;
    const error = card.querySelector('[data-mw-error]');
    if (error) error.textContent = '';

    const boxItem = isBox(btn);
    const item = { id: Number(btn.dataset.variantId), quantity: 1 };
    if (boxItem) item.properties = { [lineProperty()]: 'true' };

    add.disabled = true;
    add.setAttribute('aria-busy', 'true');
    let data = null;
    try {
      data = await post('cart/add.js', { items: [item] });
    } catch (e) {
      if (error) error.textContent = (e && e.message) || g.dataset.errorLabel;
    } finally {
      add.removeAttribute('aria-busy');
      add.disabled = false;
    }

    if (!data) {
      // The shopper may have picked another size while the request was in flight.
      setAddButton(g, card, selectedSize(card) || btn);
      return;
    }

    syncCartUI(data);
    const added = data.items ? data.items[0] : data;
    document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { cart: data, variantId: added && added.variant_id } }));
    openDrawer(data);
    getCart().then(render).catch(refresh);

    add.textContent = boxItem ? g.dataset.addedBoxLabel : g.dataset.addedCartLabel;
    add.classList.add('is-added');
    clearTimeout(add._mwTimer);
    add._mwTimer = setTimeout(() => {
      const current = selectedSize(card);
      if (current) setAddButton(g, card, current);
    }, ADDED_MS);
  };

  /* ---------- Box actions ---------- */

  const removeOne = async (btn) => {
    btn.disabled = true;
    try {
      const cart = await post('cart/change.js', { id: btn.dataset.mwRemove, quantity: Math.max(0, Number(btn.dataset.qty) - 1) });
      syncCartUI(cart);
      render(cart);
    } catch (e) {
      btn.disabled = false;
      refresh();
    }
  };

  const clearBox = async (btn) => {
    const updates = {};
    boxLines(lastCart).forEach((line) => { updates[line.key] = 0; });
    if (!Object.keys(updates).length) return;
    btn.disabled = true;
    try {
      const cart = await post('cart/update.js', { updates });
      syncCartUI(cart);
      render(cart);
    } catch (e) {
      refresh();
    } finally {
      btn.disabled = false;
    }
  };

  /* ---------- Wiring ---------- */

  // Delegated on document, so sections re-rendered by the theme editor keep working.
  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-mw-remove]');
    if (remove) { if (!remove.disabled) removeOne(remove); return; }
    const clear = e.target.closest('[data-mw-clear]');
    if (clear) { if (!clear.disabled) clearBox(clear); return; }

    const g = e.target.closest('[data-mw-grid]');
    const card = g && e.target.closest('[data-mw-card]');
    if (!card) return;
    const size = e.target.closest('.mw-size');
    if (size) { if (!size.disabled) selectSize(g, card, size); return; }
    const add = e.target.closest('[data-mw-add]');
    if (add && !add.disabled) addToCart(g, card, add);
  });

  // Keep the box in step with changes made in the cart drawer or elsewhere.
  ['rebuy:cart.change', 'on:line-item:change', 'on:cart:add'].forEach((name) => document.addEventListener(name, refresh));
  window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });
  document.addEventListener('shopify:section:load', () => {
    dockBox();
    if (lastCart) render(lastCart);
    else refresh();
  });
  document.addEventListener('shopify:section:unload', (e) => {
    if (boxSection && e.target === boxSection) boxSection = null;
  });

  const init = () => {
    dockBox();
    const embedded = document.querySelector('script[data-mw-cart]');
    let cart = null;
    try { cart = embedded ? JSON.parse(embedded.textContent) : null; } catch (e) { cart = null; }
    if (cart) render(cart);
    else refresh();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
